import React from 'react';
import { Button, Text, Flex } from '@chatsdet/ui';
import { PUCK_FOOTER_CLEARANCE } from '../../features/assistant/cornerPin';
import { LoginNodeConfig } from './node-config/LoginNodeConfig';
import { NavigateNodeConfig } from './node-config/NavigateNodeConfig';
import { ActionNodeConfig } from './node-config/ActionNodeConfig';
import { AssertionNodeConfig } from './node-config/AssertionNodeConfig';
import { SetupNodeConfig } from './node-config/SetupNodeConfig';
import { SeedNodeConfig } from './node-config/SeedNodeConfig';
import { CallFlowNodeConfig } from './node-config/CallFlowNodeConfig';
import { WaitNodeConfig } from './node-config/WaitNodeConfig';
import { ConditionNodeConfig } from './node-config/ConditionNodeConfig';
import { ApiSequenceNodeConfig } from './node-config/ApiSequenceNodeConfig';
import { SandboxActionNodeConfig } from './node-config/SandboxActionNodeConfig';
import { McpNodeConfigPanel } from './node-config/McpNodeConfigPanel';
import { NotificationNodeConfig } from './node-config/NotificationNodeConfig';
import { AudioNodeConfig } from './node-config/AudioNodeConfig';
import { AudioAssertionNodeConfig } from './node-config/AudioAssertionNodeConfig';
import { VisualCheckpointNodeConfig } from './node-config/VisualCheckpointNodeConfig';
import type { WorkflowNode } from '../../types/workflow';
import type { WorkflowInputDefinition } from '@chatsdet/types';
import type { NodeValidationError } from '../pages/FlowDesigner/validation/types';
import { sanitizeNodeDataForPersistence } from '../pages/FlowDesigner/utils';

const normalizeForComparison = (value: unknown): unknown => {
  if (value === null || typeof value !== 'object') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(normalizeForComparison);
  }
  const entries = Object.keys(value as Record<string, unknown>).sort();
  const normalized: Record<string, unknown> = {};
  for (const key of entries) {
    normalized[key] = normalizeForComparison((value as Record<string, unknown>)[key]);
  }
  return normalized;
};

const areConfigsEqual = (a: Record<string, any>, b: Record<string, any>) =>
  JSON.stringify(normalizeForComparison(a)) === JSON.stringify(normalizeForComparison(b));

function mergeInferredPageContext(
  data: Record<string, unknown>,
  pageContext: NodeConfigPanelProps['inferredPageContext']
): Record<string, unknown> {
  if (!pageContext) return data;

  const next = { ...data };
  if (!next.parentPageId && pageContext.canonicalPageId) {
    next.parentPageId = pageContext.canonicalPageId;
  }
  if (!next.canonicalPageId && pageContext.canonicalPageId) {
    next.canonicalPageId = pageContext.canonicalPageId;
  }
  if (!next.parentPageTitle && pageContext.pageTitle) {
    next.parentPageTitle = pageContext.pageTitle;
  }
  if (!next.pageTitle && pageContext.pageTitle) {
    next.pageTitle = pageContext.pageTitle;
  }
  return next;
}

interface NodeConfigPanelProps {
  isActive: boolean;
  selectedNode: WorkflowNode | null;
  inferredPageContext?: {
    canonicalPageId?: string | null;
    pageTitle?: string | null;
    pageUrl?: string | null;
  } | null;
  inferredPropertyId?: string | null;
  upstreamSeedWorkflowId?: string | null;
  workflowId?: string | null;
  workflowInputs?: WorkflowInputDefinition[];
  /** Owning flow's type — gates the inline-wait cap on wait nodes. */
  workflowFlowType?: string;
  onUpdateNode: (nodeId: string, data: Record<string, any>) => void | Promise<void>;
  onDismiss: () => void;
  validationErrors?: NodeValidationError[];
  header?: React.ReactNode;
  readOnly?: boolean;
}

export interface NodeConfigPanelHandle {
  save: () => Promise<void> | void;
  cancel: () => void;
  isSaving: boolean;
  isDirty: boolean;
}

export const NodeConfigPanel = React.forwardRef<NodeConfigPanelHandle | null, NodeConfigPanelProps>(
  (
    {
      isActive,
      selectedNode,
      inferredPageContext,
      inferredPropertyId,
      upstreamSeedWorkflowId,
      workflowId,
      workflowInputs,
      workflowFlowType,
      onUpdateNode,
      onDismiss,
      validationErrors,
      header,
      readOnly,
    },
    ref
  ) => {
    const [localData, setLocalData] = React.useState<Record<string, any>>({});
    const [isDirty, setIsDirty] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [focusKey, setFocusKey] = React.useState(0);
    const initialDataRef = React.useRef<Record<string, any>>({});
    const prevSelectedNodeIdRef = React.useRef<string | null>(null);
    const isDirtyRef = React.useRef(false);

    const sanitizeNodeData = React.useCallback(
      (data?: Record<string, any> | null) => sanitizeNodeDataForPersistence(data),
      []
    );

    React.useEffect(() => {
      isDirtyRef.current = isDirty;
    }, [isDirty]);

    React.useEffect(() => {
      if (!selectedNode) {
        prevSelectedNodeIdRef.current = null;
        initialDataRef.current = {};
        setLocalData((prev) => (Object.keys(prev).length === 0 ? prev : {}));
        setIsDirty(false);
        return;
      }

      const sanitized = sanitizeNodeData(selectedNode.data as Record<string, any>);
      const initialData =
        selectedNode.type === 'action'
          ? mergeInferredPageContext(sanitized, inferredPageContext)
          : sanitized;
      const nodeId = selectedNode.id;
      const nodeChanged = prevSelectedNodeIdRef.current !== nodeId;
      const persistedDataChanged = !areConfigsEqual(initialDataRef.current, initialData);

      if (nodeChanged) {
        prevSelectedNodeIdRef.current = nodeId;

        // Read-once acknowledgment: when the user first opens an action node with
        // an unacknowledged AI improvement, record the timestamp so the canvas
        // sparkle icon stops showing. All original data is preserved.
        if (
          !readOnly &&
          selectedNode.type === 'action' &&
          initialData.guidanceImprovedAt &&
          !initialData.guidanceAcknowledgedAt
        ) {
          const acknowledged = { ...initialData, guidanceAcknowledgedAt: new Date().toISOString() };
          initialDataRef.current = acknowledged;
          setLocalData(acknowledged);
          onUpdateNode(nodeId, acknowledged);
        } else {
          initialDataRef.current = initialData;
          setLocalData(initialData);
        }

        setIsDirty(false);
        setFocusKey((k) => k + 1);
        return;
      }

      const localDataIsClean = areConfigsEqual(
        sanitizeNodeData(localData),
        initialDataRef.current
      );
      if (persistedDataChanged && localDataIsClean) {
        initialDataRef.current = initialData;
        setLocalData(initialData);
        setIsDirty(false);
      }
    }, [selectedNode, sanitizeNodeData, inferredPageContext, localData]);

    React.useEffect(() => {
      if (
        !selectedNode ||
        selectedNode.type !== 'action' ||
        !inferredPageContext ||
        (!inferredPageContext.canonicalPageId && !inferredPageContext.pageTitle)
      ) {
        return;
      }

      setLocalData((prev) => {
        const next = mergeInferredPageContext(prev, inferredPageContext);
        if (areConfigsEqual(prev, next)) return prev;

        // Inferred fields are baseline context, so they must not stay dirty
        // even when they arrive alongside a real edit. Merge only missing
        // context fields to preserve any user-authored baseline values.
        initialDataRef.current = sanitizeNodeData(
          mergeInferredPageContext(initialDataRef.current, inferredPageContext)
        );
        return next;
      });
    }, [inferredPageContext?.canonicalPageId, inferredPageContext?.pageTitle, selectedNode]);

    React.useEffect(() => {
      const sanitizedLocal = sanitizeNodeData(localData);
      const dirty = !areConfigsEqual(sanitizedLocal, initialDataRef.current);
      if (dirty !== isDirty) {
        setIsDirty(dirty);
      }
    }, [localData, sanitizeNodeData, isDirty]);

    const handleSave = React.useCallback(async () => {
      if (!selectedNode || !isDirtyRef.current) {
        return;
      }
      setIsSaving(true);
      try {
        const maybePromise = onUpdateNode(selectedNode.id, localData);
        if (maybePromise && typeof (maybePromise as PromiseLike<void>).then === 'function') {
          await maybePromise;
        }
        const sanitized = sanitizeNodeData(localData);
        initialDataRef.current = sanitized;
        setIsDirty(false);
      } finally {
        setIsSaving(false);
      }
    }, [selectedNode, localData, onUpdateNode, sanitizeNodeData]);

    const handleCancel = React.useCallback(() => {
      setLocalData(initialDataRef.current);
      setIsDirty(false);
      onDismiss();
    }, [onDismiss]);

    React.useImperativeHandle(
      ref,
      () => ({
        save: () => {
          return handleSave();
        },
        cancel: () => {
          handleCancel();
        },
        get isSaving() {
          return isSaving;
        },
        get isDirty() {
          return isDirtyRef.current;
        },
      }),
      [handleSave, handleCancel, isSaving]
    );

    // Get propertyId from localData or infer from upstream login node
    const propertyIdFromConfig = localData.propertyId as string | undefined;
    const effectivePropertyId = propertyIdFromConfig ?? inferredPropertyId ?? undefined;

    const renderContent = () => {
      if (!selectedNode) {
        return <Text className="text-muted-foreground">No node selected</Text>;
      }

      switch (selectedNode.type) {
        case 'login':
          return (
            <LoginNodeConfig
              isActive={isActive}
              localData={localData}
              setLocalData={setLocalData}
              upstreamSeedWorkflowId={upstreamSeedWorkflowId}
              workflowInputs={workflowInputs}
            />
          );
        case 'navigate':
          return (
            <NavigateNodeConfig
              localData={localData}
              setLocalData={setLocalData}
            />
          );
        case 'action':
          return (
            <ActionNodeConfig
              focusKey={focusKey}
              propertyId={effectivePropertyId}
              isActive={isActive}
              localData={localData}
              setLocalData={setLocalData}
              inferredPageContext={inferredPageContext}
              onSave={handleSave}
            />
          );
        case 'assertion':
          return (
            <AssertionNodeConfig
              focusKey={focusKey}
              localData={localData}
              setLocalData={setLocalData}
            />
          );
        case 'setup':
          return (
            <SetupNodeConfig
              isActive={isActive}
              localData={localData}
              setLocalData={setLocalData}
              workflowId={workflowId ?? undefined}
              onImmediateUpdate={(data) => {
                if (selectedNode) {
                  const sanitized = sanitizeNodeData(data);
                  onUpdateNode(selectedNode.id, sanitized);
                  initialDataRef.current = sanitized;
                  setIsDirty(false);
                }
              }}
            />
          );
        case 'seed':
          return (
            <SeedNodeConfig
              isActive={isActive}
              localData={localData}
              setLocalData={setLocalData}
              workflowId={workflowId ?? undefined}
              onImmediateUpdate={(data) => {
                if (selectedNode) {
                  const sanitized = sanitizeNodeData(data);
                  onUpdateNode(selectedNode.id, sanitized);
                  initialDataRef.current = sanitized;
                  setIsDirty(false);
                }
              }}
            />
          );
        case 'call_flow':
          return (
            <CallFlowNodeConfig
              isActive={isActive}
              localData={localData}
              setLocalData={setLocalData}
              workflowId={workflowId ?? undefined}
              onImmediateUpdate={(data) => {
                if (selectedNode) {
                  const sanitized = sanitizeNodeData(data);
                  onUpdateNode(selectedNode.id, sanitized);
                  initialDataRef.current = sanitized;
                  setIsDirty(false);
                }
              }}
            />
          );
        case 'wait':
          return (
            <WaitNodeConfig
              localData={localData}
              setLocalData={setLocalData}
              flowType={workflowFlowType}
            />
          );
        case 'condition':
          return (
            <ConditionNodeConfig
              focusKey={focusKey}
              localData={localData}
              setLocalData={setLocalData}
              workflowInputs={workflowInputs}
            />
          );
        case 'api_sequence':
          return (
            <ApiSequenceNodeConfig
              localData={localData}
              setLocalData={setLocalData}
              propertyId={effectivePropertyId}
              nodeId={selectedNode.id}
              workflowId={workflowId ?? undefined}
              onSave={(data) => {
                if (!selectedNode) return;
                initialDataRef.current = sanitizeNodeData(data);
                setIsDirty(false);
                onUpdateNode(selectedNode.id, data);
              }}
            />
          );
        case 'sandbox_action':
          return (
            <SandboxActionNodeConfig localData={localData} setLocalData={setLocalData} />
          );
        case 'mcp':
          return <McpNodeConfigPanel localData={localData} setLocalData={setLocalData} />;
        case 'end':
          return <EndNodeConfigInline localData={localData} setLocalData={setLocalData} />;
        case 'notification':
          return (
            <NotificationNodeConfig localData={localData} setLocalData={setLocalData} />
          );
        case 'audio':
          return <AudioNodeConfig localData={localData} setLocalData={setLocalData} />;
        case 'audio-assertion':
          return <AudioAssertionNodeConfig localData={localData} setLocalData={setLocalData} />;
        case 'visual_checkpoint':
          return <VisualCheckpointNodeConfig localData={localData} setLocalData={setLocalData} />;
        default:
          return (
            <Text className="text-muted-foreground">Unknown node type: {selectedNode.type}</Text>
          );
      }
    };

    return (
      <div className="flex flex-col flex-1 min-h-0 gap-4">
        {validationErrors && validationErrors.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 space-y-1 mx-4 mt-4">
            {validationErrors.map((err, i) => (
              <p key={i} className="text-sm text-destructive font-medium">
                {err.message}
              </p>
            ))}
          </div>
        )}
        <div className="flex-1 overflow-y-auto px-4 pt-2">
          {header}
          {readOnly ? (
            <fieldset disabled className="contents">
              {renderContent()}
            </fieldset>
          ) : (
            renderContent()
          )}
        </div>
        {!readOnly && (
          <Flex
            gap="sm"
            justify="end"
            // The assistant puck is pinned over the bottom-right corner of the
            // viewport (where this footer sits); leave its width clear so it
            // never intercepts Cancel/Save.
            style={{ paddingRight: PUCK_FOOTER_CLEARANCE }}
            className="shrink-0 border-t border-border pt-4 pb-4 pl-4"
          >
            <Button variant="outline" onPress={handleCancel}>
              Cancel
            </Button>
            <Button variant="primary" onPress={handleSave} isDisabled={!selectedNode || !isDirty}>
              Save
            </Button>
          </Flex>
        )}
      </div>
    );
  }
);

NodeConfigPanel.displayName = 'NodeConfigPanel';

/** Inline config for end nodes - just a pass/fail toggle */
function EndNodeConfigInline({
  localData,
  setLocalData,
}: {
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}) {
  const outcome = (localData.outcome as string) ?? 'pass';
  return (
    <Flex direction="column" gap="md">
      <Text className="text-sm font-medium">Outcome</Text>
      <Text className="text-xs text-muted-foreground">
        Choose whether this end node marks the workflow as passed or failed.
      </Text>
      <Flex gap="sm">
        <Button
          variant={outcome === 'pass' ? 'primary' : 'outline'}
          size="sm"
          onPress={() => setLocalData((prev) => ({ ...prev, outcome: 'pass' }))}
        >
          Pass
        </Button>
        <Button
          variant={outcome === 'fail' ? 'destructive' : 'outline'}
          size="sm"
          onPress={() => setLocalData((prev) => ({ ...prev, outcome: 'fail' }))}
        >
          Fail
        </Button>
      </Flex>
    </Flex>
  );
}
